import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";

const EMPTY = {
  First_Name: "",
  Last_Name: "",
  Country_Code: "+91",
  Mobile_Number: "",
  Email: "",
  Property_Service: "",
  Tenant_Project_Name: "",
  Property_Location: "",
  Requirement_Type: "",
  Property_Type_Id: "",
  Budget_Min: "",
  Budget_Max: "",
  Preferred_Location: "",
  Site_Visit: false,
  Notes: "",
};

function RegisterLead() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [tenantProjects, setTenantProjects] = useState([]);
  const [requirementTypes, setRequirementTypes] = useState(["Buy", "Rent", "Lease"]);
  const [countries, setCountries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const phoneCountryOptions = useMemo(() => {
    const mapped = (countries || [])
      .map((c) => ({
        id: c.country_id ?? c.Country_Id ?? c.id,
        code: String(c.country_code || c.country_short_code || c.Country_Code || "").trim(),
        name: c.country_name || c.Country_Name || c.name || "",
      }))
      .filter((x) => x.code);
    const codes = new Set(["+91"]);
    mapped.forEach((c) => {
      const code = c.code.startsWith("+") ? c.code : `+${c.code}`;
      codes.add(code);
    });
    return Array.from(codes).sort((a, b) => a.localeCompare(b));
  }, [countries]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [metaRes, countriesRes] = await Promise.all([
          axiosInstance.get("/client/lead-meta"),
          axiosInstance.get("/countries").catch(() => ({ data: [] })),
        ]);
        if (!active) return;
        const data = metaRes.data?.data || {};
        setPropertyTypes(Array.isArray(data.propertyTypes) ? data.propertyTypes : []);
        setTenantProjects(Array.isArray(data.tenantProjects) ? data.tenantProjects : []);
        if (Array.isArray(data.requirementTypes) && data.requirementTypes.length) {
          setRequirementTypes(data.requirementTypes);
        }
        const rawCountries = Array.isArray(countriesRes?.data)
          ? countriesRes.data
          : Array.isArray(countriesRes?.data?.data)
            ? countriesRes.data.data
            : [];
        setCountries(rawCountries);
      } catch (_) {
        // Meta is optional; form still works with free-text fields.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const projectOptionLabel = (project) =>
    [project.Short_Code, project.Project_Name].filter(Boolean).join(" — ");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.First_Name.trim()) {
      setError("First name is required.");
      return;
    }
    const mobileError = mobileValidationMessage(form.Mobile_Number, { required: true });
    if (mobileError) {
      setError(mobileError);
      return;
    }

    const countryCode = (() => {
      const raw = String(form.Country_Code || "+91").trim();
      if (!raw) return "+91";
      return raw.startsWith("+") ? raw : `+${raw}`;
    })();

    const payload = {
      First_Name: form.First_Name.trim(),
      Last_Name: form.Last_Name.trim() || null,
      Country_Code: countryCode,
      Mobile_Number: form.Mobile_Number.trim(),
      Email: form.Email.trim() || null,
      Property_Service: form.Property_Service.trim() || null,
      Tenant_Project_Name: form.Tenant_Project_Name.trim() || null,
      Property_Location: form.Property_Location.trim() || null,
      Requirement_Type: form.Requirement_Type || null,
      Property_Type_Id: form.Property_Type_Id || null,
      Budget_Min: form.Budget_Min !== "" ? form.Budget_Min : null,
      Budget_Max: form.Budget_Max !== "" ? form.Budget_Max : null,
      Preferred_Location: form.Preferred_Location.trim() || null,
      Site_Visit: !!form.Site_Visit,
      Notes: form.Notes.trim() || null,
    };

    setSaving(true);
    try {
      await axiosInstance.post("/client/leads", payload);
      setSuccess("Lead registered successfully.");
      setTimeout(() => navigate("/portal/leads"), 700);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Could not register lead.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cportal-page">
      <div className="cportal-page-head">
        <h1>Register Lead</h1>
      </div>

      {error && <div className="cportal-alert cportal-alert-error">{error}</div>}
      {success && <div className="cportal-alert cportal-alert-success">{success}</div>}

      <form className="cportal-card" onSubmit={handleSubmit}>
        <h2 className="cportal-form-section">Contact information</h2>
        <div className="cportal-form-grid">
          <div className="cportal-field">
            <label>First name <span className="cportal-required">*</span></label>
            <input value={form.First_Name} onChange={(e) => setField("First_Name", e.target.value)} required />
          </div>
          <div className="cportal-field">
            <label>Last name</label>
            <input value={form.Last_Name} onChange={(e) => setField("Last_Name", e.target.value)} />
          </div>
          <div className="cportal-field cportal-field-phone">
            <label>Mobile number <span className="cportal-required">*</span></label>
            <div className="cportal-phone-row">
              <select
                value={form.Country_Code}
                onChange={(e) => setField("Country_Code", e.target.value)}
                aria-label="Country code"
              >
                {phoneCountryOptions.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
              <input
                value={form.Mobile_Number}
                onChange={(e) => setField("Mobile_Number", sanitizeMobileInput(e.target.value))}
                inputMode="numeric"
                maxLength={10}
                placeholder="Mobile number"
                required
              />
            </div>
          </div>
          <div className="cportal-field">
            <label>Email</label>
            <input
              type="email"
              value={form.Email}
              onChange={(e) => setField("Email", e.target.value)}
              placeholder="Email address"
            />
          </div>
        </div>

        <h2 className="cportal-form-section">Requirement</h2>
        <div className="cportal-form-grid">
          <div className="cportal-field">
            <label>Service</label>
            <input value={form.Property_Service} onChange={(e) => setField("Property_Service", e.target.value)} />
          </div>
          <div className="cportal-field">
            <label>Project</label>
            <select
              value={form.Tenant_Project_Name}
              onChange={(e) => setField("Tenant_Project_Name", e.target.value)}
            >
              <option value="">Select project…</option>
              {tenantProjects.map((project) => (
                <option key={project.Tenant_Project_Id} value={project.Project_Name}>
                  {projectOptionLabel(project)}
                </option>
              ))}
            </select>
          </div>
          <div className="cportal-field">
            <label>Property location</label>
            <input value={form.Property_Location} onChange={(e) => setField("Property_Location", e.target.value)} />
          </div>
          <div className="cportal-field">
            <label>Requirement type</label>
            <select value={form.Requirement_Type} onChange={(e) => setField("Requirement_Type", e.target.value)}>
              <option value="">Select…</option>
              {requirementTypes.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="cportal-field">
            <label>Property type</label>
            <select value={form.Property_Type_Id} onChange={(e) => setField("Property_Type_Id", e.target.value)}>
              <option value="">Select…</option>
              {propertyTypes.map((p) => (
                <option key={p.Property_Type_Id} value={p.Property_Type_Id}>
                  {p.Property_Type_Name}
                </option>
              ))}
            </select>
          </div>
          <div className="cportal-field">
            <label>Budget min</label>
            <input
              type="number"
              value={form.Budget_Min}
              onChange={(e) => setField("Budget_Min", e.target.value)}
            />
          </div>
          <div className="cportal-field">
            <label>Budget max</label>
            <input
              type="number"
              value={form.Budget_Max}
              onChange={(e) => setField("Budget_Max", e.target.value)}
            />
          </div>
          <div className="cportal-field">
            <label>Lead location</label>
            <input value={form.Preferred_Location} onChange={(e) => setField("Preferred_Location", e.target.value)} />
          </div>
          <div className="cportal-field cportal-field-check">
            <label>
              <input
                type="checkbox"
                checked={form.Site_Visit}
                onChange={(e) => setField("Site_Visit", e.target.checked)}
              />
              <span>Site visit</span>
            </label>
          </div>
        </div>

        <h2 className="cportal-form-section">Notes</h2>
        <div className="cportal-field">
          <textarea rows={3} value={form.Notes} onChange={(e) => setField("Notes", e.target.value)} />
        </div>

        <div className="cportal-form-actions">
          <button type="button" className="cportal-btn cportal-btn-ghost" onClick={() => navigate("/portal/leads")}>
            Cancel
          </button>
          <button type="submit" className="cportal-btn cportal-btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Register Lead"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default RegisterLead;
